import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, MapPin, LogOut, Menu, X, User, ArrowRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API, toast } from '@/App';
import TrackingSearch from '@/components/shared/TrackingSearch';
import NotificationBell from '@/components/shared/NotificationBell';
import { clearAuthSession } from '@/utils/authSession';

const AgentPRDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [relayPoint, setRelayPoint] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('packages'); // nouveau state

  useEffect(() => {
    loadUserData();
    loadDeliveries();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Load relay point info
      if (response.data.relay_point_id) {
        const rpResponse = await axios.get(`${API}/relay-points`);
        const rp = rpResponse.data.find(r => r.id === response.data.relay_point_id);
        setRelayPoint(rp);
      }
    } catch (_error) {
      console.warn('Impossible de charger le profil agent PR.');
    }
  };

  const loadDeliveries = async () => {
    try {
      const response = await axios.get(`${API}/deliveries`);
      setDeliveries(response.data.filter(d => d.status === 'at_relay' || d.status === 'transferred'));
    } catch (_error) {
      console.warn('Impossible de charger les livraisons agent PR.');
    }
  };

  const handleLogout = () => {
    clearAuthSession();
    navigate('/');
    toast.success('À bientôt!');
  };

  const getStatusBadge = (status) => {
    return status === 'at_relay' ? 
      <Badge className="bg-indigo-100 text-indigo-700">Au Point de Relais</Badge> :
      <Badge className="bg-purple-100 text-purple-700">Transféré</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="h-full flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold gradient-text">Coli Noki</h1>
                  <p className="text-xs text-gray-500">Agent P-R</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {relayPoint && (
            <div className="p-4">
              <Card className="bg-gradient-to-br from-red-50 to-cyan-50 border-red-200">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-gray-700 mb-1">Point de Relais</p>
                  <p className="font-bold text-red-600">{relayPoint.name}</p>
                  <p className="text-xs text-gray-600 mt-1">{relayPoint.address}</p>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="flex-1 p-4">
            <div className="space-y-2">
              <Button
                variant={activeTab === 'packages' ? 'default' : 'ghost'}
                className="w-full justify-start"
                onClick={() => setActiveTab('packages')}
              >
                <Package className="w-4 h-4 mr-2" />
                Colis au point relais
              </Button>
              <Button
                variant={activeTab === 'tracking' ? 'default' : 'ghost'}
                className="w-full justify-start"
                onClick={() => setActiveTab('tracking')}
              >
                <Search className="w-4 h-4 mr-2" />
                Suivi de colis
              </Button>
            </div>
          </div>

          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.full_name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="outline"
              className="w-full"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h2 className="text-2xl font-bold">
              {activeTab === 'packages' ? 'Colis au Point de Relais' : 'Suivi de colis'}
            </h2>
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto">
          {activeTab === 'tracking' ? (
            <TrackingSearch />
          ) : (
            <>
              <div className="mb-6">
                <Card className="bg-gradient-to-br from-red-500 to-cyan-500 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-2xl font-bold mb-1">{deliveries.length}</h3>
                        <p className="text-cyan-50">Colis en transit</p>
                      </div>
                      <Package className="w-12 h-12 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

          {deliveries.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucun colis au Point de Relais</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {deliveries.map((delivery) => (
                <Card key={delivery.id} className="card-hover">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">Colis #{delivery.id.slice(0, 8)}</CardTitle>
                        <CardDescription>Vendeur: {delivery.vendeur_name}</CardDescription>
                      </div>
                      {getStatusBadge(delivery.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-red-600" />
                        <span className="font-medium">{delivery.pickup_city}</span>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                        <MapPin className="w-4 h-4 text-green-600" />
                        <span className="font-medium">{delivery.delivery_city}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                        <div className="text-sm">
                          <p className="text-gray-500">Poids</p>
                          <p className="font-medium">{delivery.weight_verified || delivery.weight} kg</p>
                        </div>
                        <div className="text-sm">
                          <p className="text-gray-500">Client</p>
                          <p className="font-medium truncate">{delivery.client_info.full_name}</p>
                        </div>
                      </div>

                      <div className="bg-gray-50 p-3 rounded text-sm">
                        <p className="text-gray-600">{delivery.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
            </>
          )}
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AgentPRDashboard;
